import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { API, authHeaders } from "@/lib/adminApi";

/* ── Stripe Connect Section (compact — redirects to dedicated onboarding page) ── */
const StripeConnectSection = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [amountInput, setAmountInput] = useState("");
  const [savingAmount, setSavingAmount] = useState(false);

  useEffect(() => {
    axios.get(`${API}/associations/me/stripe-status`, { headers: authHeaders() })
      .then(res => {
        setStatus(res.data);
        setAmountInput(res.data.cotisation_amount != null ? String(res.data.cotisation_amount) : "");
      })
      .catch(() => {});
  }, []);

  const handleSaveAmount = async () => {
    const amount = parseFloat(amountInput);
    if (!amount || amount <= 0) { toast.error("Le montant doit être positif."); return; }
    setSavingAmount(true);
    try {
      await axios.patch(`${API}/associations/me/cotisation-amount`, { cotisation_amount: amount }, { headers: authHeaders() });
      toast.success("Montant de cotisation mis à jour.");
      setStatus(s => ({ ...s, cotisation_amount: amount }));
    } catch {
      toast.error("Erreur lors de la mise à jour.");
    } finally {
      setSavingAmount(false);
    }
  };

  const kyc = status?.stripe_kyc_status ?? "not_started";

  const stripeStateBadge = () => {
    if (kyc === "active") return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />Actif
      </span>
    );
    if (kyc === "pending" || kyc === "restricted") return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />Vérification en cours
      </span>
    );
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-500">
        <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />Non connecté
      </span>
    );
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Cotisation amount */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">
            Montant annuel de cotisation
          </p>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <input
                type="number" min="1" step="0.01"
                value={amountInput}
                onChange={e => setAmountInput(e.target.value)}
                placeholder="ex. 50"
                className="w-full pl-4 pr-10 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium">€</span>
            </div>
            <button
              onClick={handleSaveAmount}
              disabled={savingAmount || !amountInput}
              className="px-4 py-3 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-[0.97] disabled:opacity-50"
              style={{ backgroundColor: "#059669" }}
            >
              {savingAmount ? "…" : "Enregistrer"}
            </button>
          </div>
          {status?.cotisation_amount != null && (
            <p className="mt-2 text-xs text-slate-400">
              Actuel : <span className="font-semibold text-slate-600">{status.cotisation_amount.toLocaleString("fr-FR")} €</span>
              {" · "}SEPA mensuel : <span className="font-semibold text-slate-600">{(status.cotisation_amount / 12).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €/mois</span>
            </p>
          )}
        </div>

        {/* Stripe status — compact */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">
            Compte Stripe Connect
          </p>
          <div className="flex items-center justify-between gap-4">
            <div>{stripeStateBadge()}</div>
            <button
              onClick={() => navigate("/app/stripe-onboarding")}
              className="text-xs font-semibold px-4 py-2 rounded-xl border transition-all hover:bg-slate-50"
              style={{ borderColor: "#635BFF", color: "#635BFF" }}
            >
              {kyc === "not_started" ? "Activer Stripe →" : "Gérer →"}
            </button>
          </div>
          {kyc === "active" && (
            <p className="mt-2 text-xs text-slate-400">
              Cotisations encaissées directement sur le compte de l'association.
            </p>
          )}
          {(kyc === "pending" || kyc === "restricted") && (
            <p className="mt-2 text-xs text-amber-600">
              Vérification en cours chez Stripe. PayPal reste disponible en attendant.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default StripeConnectSection;
